import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { callAjaxIO } from 'Shared/ajax_io';
import type { PatrolLanguageFilter, PatrolQueues } from './patrol_types';

// The server-rendered snapshot ages from page load, not from whenever the
// cache entry is (re)created — without this, a garbage-collected entry
// reseeded from props would present a worked-off backlog as fresh.
const pageLoadedAt = Date.now();

/**
 * All three queues for one language filter. The filter is a SQL condition,
 * so switching it costs a round trip; the queues it returns are small
 * enough to page through in the browser. The server seeds the initial
 * filter into props, so only other filters fetch, and keepPreviousData
 * leaves the current list up while the next one loads.
 *
 * The staleTime is short because patrolling is a shared queue: it only has
 * to cover the first paint, so the seeded snapshot isn't refetched the
 * moment it arrives, and after that a return to the page re-asks rather
 * than offering a backlog someone else already worked off.
 */
export function usePatrolQueues(filter: PatrolLanguageFilter, initial: PatrolQueues) {
   return useQuery({
      queryKey: ['patrol', filter],
      queryFn: () => callAjaxIO<PatrolQueues>('getPatrolQueues', { filter }),
      initialData: filter === initial.filter ? initial : undefined,
      initialDataUpdatedAt: pageLoadedAt,
      placeholderData: keepPreviousData,
      staleTime: 30_000,
   });
}
